import React, { useEffect, useMemo, useState } from 'react';
import { Search, Library as LibIcon, Plus, ArrowRight, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Strategic Library — full browse for Idris's per-sector reading shelves.
 *
 * Features
 * • Counters: total · today's adds · readings per AI per day
 * • Filter chips: ALL + every persona (with their count)
 * • Type filter: book · paper · publication · report
 * • Search box (title + author)
 * • "Add a book" — type a title; Idris finds + classifies + shelves it
 *   on the right AI's reading list.
 */
const TYPES = [
  { id: '',            label: 'All types' },
  { id: 'book',        label: 'Books' },
  { id: 'paper',       label: 'Papers' },
  { id: 'publication', label: 'Publications' },
  { id: 'report',      label: 'Reports' },
];

const PERSONA_LABEL = {
  yusuf: "Yusuf", khadija: "Khadija", mohammed: "Mohammed", yacob: "Yacob",
  maryam: "Maryam", nouh: "Nouh", fatima: "Fatima", ibrahim: "Ibrahim",
  sulaiman: "Sulaiman", musa: "Musa", bilqis: "Bilqis", isa: "Isa",
  dawud: "Dawud", harun: "Harun", shuaib: "Shu'aib", idris: "Idris",
  wujdan: "Wujdān", iqra: "Iqra", yunus: "Yunus", ayyub: "Ayyub",
  ismail: "Ismail", hud: "Hud", salih: "Salih", adam: "Adam",
  zakariyya: "Zakariyya", ilyas: "Ilyas",
};

export default function Library() {
  /* eslint-disable -- v6 react-hooks/set-state-in-effect + purity false-positives on debounce/fetch effects */
  const [persona, setPersona] = useState('');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 80 };
      if (persona) params.persona = persona;
      if (type)    params.type = type;
      if (debouncedQ) params.q = debouncedQ;
      const { data } = await api.get('/ai/mentors/library', { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load library.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [persona, type, debouncedQ]);

  const personaList = useMemo(() => {
    const byP = data?.summary?.by_persona || {};
    return Object.entries(byP).sort((a, b) => b[1] - a[1]);
  }, [data]);

  return (
    <div className="ve-container py-10" data-testid="library-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Strategic Library</div>
          <h1 className="display text-4xl sm:text-5xl mt-1 leading-[1.02]">Idris&rsquo;s reading shelves.</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-white/60 max-w-xl">
            Every AI officer gets {data?.summary?.books_per_persona_per_day ?? 4} curated readings per day —
            books, papers, publications and reports — plus a one-line teaching plan baked into their next chat.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          data-testid="library-add-book-btn"
          className="ve-btn-primary"
        >
          <Plus size={14}/> Add a book
        </button>
      </div>

      {/* Counters */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="ve-card p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Library size</div>
          <div className="display text-3xl mt-1 ve-gold-text" data-testid="lib-page-total">{(data?.summary?.total ?? 0).toLocaleString()}</div>
        </div>
        <div className="ve-card p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Added today</div>
          <div className="display text-3xl mt-1">+{(data?.summary?.today_count ?? 0).toLocaleString()}</div>
        </div>
        <div className="ve-card p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Per AI · per day</div>
          <div className="display text-3xl mt-1">{data?.summary?.books_per_persona_per_day ?? '—'}</div>
        </div>
        <div className="ve-card p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Filtered results</div>
          <div className="display text-3xl mt-1">{data?.total ?? '—'}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 ve-card p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title or author…"
              data-testid="library-search"
              className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-white/5 border border-slate-200 dark:border-white/15 rounded-md focus:outline-none focus:border-[#8B3FC5]"
            />
          </div>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            data-testid="library-type-filter"
            className="text-sm px-3 py-2 rounded-md border border-slate-200 dark:border-white/15"
          >
            {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          {(persona || type || debouncedQ) && (
            <button
              type="button"
              onClick={() => { setPersona(''); setType(''); setQ(''); }}
              data-testid="library-clear-filters"
              className="text-xs uppercase tracking-[0.18em] text-slate-500 hover:text-[#5E1F8B]"
            >
              <X size={12} className="inline -mt-0.5"/> Clear
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            onClick={() => setPersona('')}
            data-testid="library-shelf-all"
            className={`text-[11px] uppercase tracking-[0.15em] font-bold px-2.5 py-1.5 rounded border transition-colors ${persona === '' ? 'bg-[#8B3FC5] text-white border-[#8B3FC5]' : 'bg-white dark:bg-white/5 text-slate-700 border-slate-200 hover:border-[#8B3FC5]'}`}
          >
            All shelves · {(data?.summary?.total ?? 0).toLocaleString()}
          </button>
          {personaList.map(([p, n]) => (
            <button
              key={p}
              type="button"
              onClick={() => setPersona(p)}
              data-testid={`library-shelf-${p}`}
              className={`text-[11px] uppercase tracking-[0.15em] font-bold px-2.5 py-1.5 rounded border transition-colors ${persona === p ? 'bg-[#8B3FC5] text-white border-[#8B3FC5]' : 'bg-white dark:bg-white/5 text-slate-700 border-slate-200 hover:border-[#8B3FC5]'}`}
            >
              {PERSONA_LABEL[p] || p} · {n}
            </button>
          ))}
        </div>
      </div>

      {/* Book list */}
      <div className="mt-6">
        {loading && (
          <div className="ve-card p-10 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Loading shelves…
          </div>
        )}
        {!loading && (data?.books?.length ?? 0) === 0 && (
          <div className="ve-card p-10 text-center text-sm text-slate-500">
            <LibIcon size={28} className="mx-auto mb-3 opacity-30"/>
            No readings match. Try clearing filters or <button className="underline hover:text-[#5E1F8B]" onClick={() => setShowAdd(true)}>add one yourself</button>.
          </div>
        )}
        {!loading && (data?.books?.length ?? 0) > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="library-results">
            {data.books.map((b) => <BookCard key={b.book_id} b={b}/>)}
          </div>
        )}
      </div>

      {/* Add modal */}
      {showAdd && <AddBookModal onClose={() => setShowAdd(false)} onAdded={(book) => { setShowAdd(false); load(); if (book?.target_persona) setPersona(book.target_persona); }}/>}
    </div>
  );
}

function BookCard({ b }) {
  return (
    <div
      className="ve-card p-4 hover:border-[#8B3FC5] transition-colors"
      data-testid={`library-card-${b.book_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5E1F8B] font-bold">
            {PERSONA_LABEL[b.target_persona] || b.target_persona || b.domain}
            {b.type && <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[9px] bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-white/80">{b.type}</span>}
          </div>
          <h3 className="font-bold mt-1 leading-snug" title={b.title}>{b.title}</h3>
          <div className="text-xs text-slate-500 mt-0.5 truncate">
            {b.author}{b.year ? ` · ${b.year}` : ''}{b.venue ? ` · ${b.venue}` : ''}
          </div>
        </div>
        {b.added_by === 'user' && (
          <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 rounded font-bold whitespace-nowrap">manual</span>
        )}
      </div>
      {b.summary && <p className="text-sm text-slate-600 dark:text-white/70 mt-2 leading-snug">{b.summary}</p>}
      {b.key_insight && (
        <div className="mt-2 pt-2 border-t border-slate-100 dark:border-white/10">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Key insight</div>
          <div className="text-xs text-slate-700 dark:text-white/85 leading-snug">{b.key_insight}</div>
        </div>
      )}
    </div>
  );
}

function AddBookModal({ onClose, onAdded }) {
  const [title, setTitle] = useState('');
  const [hint, setHint] = useState('');
  const [persona, setPersona] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/ai/mentors/library/ingest-by-title', {
        title: title.trim(),
        hint: hint.trim() || undefined,
        persona: persona || undefined,
      }, { timeout: 90000 });
      if (data?.ok) {
        toast.success(`Idris shelved "${data.book.title}" with ${PERSONA_LABEL[data.book.target_persona] || data.book.target_persona}.`);
        onAdded?.(data.book);
      } else if (data?.reason === 'already_in_library') {
        toast.info(`That work is already on ${PERSONA_LABEL[data.book?.target_persona] || data.book?.target_persona}'s shelf.`);
      } else if (data?.reason === 'not_found') {
        toast.error(`Idris couldn't verify that book. Closest guess: ${data?.guess?.corrected_title || '—'}.`);
      } else {
        toast.error(`Could not ingest: ${data?.reason || 'unknown error'}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ingest failed.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="ve-card max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="library-add-modal"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Add a book</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-[#5E1F8B]" data-testid="library-add-close"><X size={18}/></button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Type the title. Idris will verify the work is real, classify it to the right AI&rsquo;s shelf, and write the metadata.
        </p>
        <form className="mt-4 space-y-3" onSubmit={submit}>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Title</label>
            <input
              type="text"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder='e.g. "Thinking, Fast and Slow"'
              data-testid="library-add-title"
              className="mt-1 w-full px-3 py-2 text-sm bg-white dark:bg-white/5 border border-slate-200 dark:border-white/15 rounded-md focus:outline-none focus:border-[#8B3FC5]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Hint <span className="text-slate-400 normal-case">(optional)</span></label>
            <input
              type="text"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder="author, year, publisher — helps disambiguate"
              data-testid="library-add-hint"
              className="mt-1 w-full px-3 py-2 text-sm bg-white dark:bg-white/5 border border-slate-200 dark:border-white/15 rounded-md focus:outline-none focus:border-[#8B3FC5]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Shelf <span className="text-slate-400 normal-case">(optional — Idris will auto-pick if blank)</span></label>
            <select
              value={persona}
              onChange={(e) => setPersona(e.target.value)}
              data-testid="library-add-persona"
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border border-slate-200 dark:border-white/15"
            >
              <option value="">— Let Idris choose —</option>
              {Object.entries(PERSONA_LABEL).map(([k, v]) => <option key={k} value={k}>{v} ({k})</option>)}
            </select>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} data-testid="library-add-cancel" className="ve-btn-secondary text-sm">Cancel</button>
            <button type="submit" disabled={submitting || !title.trim()} data-testid="library-add-submit" className="ve-btn-primary text-sm disabled:opacity-50">
              {submitting ? <><Loader2 size={14} className="animate-spin"/> Looking up…</> : <>Shelve it <ArrowRight size={14}/></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
